import type { AssistantRuntime, Tool } from '@assistant-ui/react';
import { AssistantChatTransport, frontendTools } from '@assistant-ui/react-ai-sdk';
import {
  type ChatRequestOptions,
  type ChatTransport,
  createIdGenerator,
  type HttpChatTransportInitOptions,
  type LanguageModel,
  safeValidateUIMessages,
  streamText,
  type ToolSet,
  toUIMessageStream,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';
import dayjs from 'dayjs';

import { renderThreadTranscript } from './renderThreadTranscript.tsx';
import type { AtlasMessage } from './types.ts';
import {
  applyTurnRequest,
  createTranscript,
  type FlowResolverEntry,
  resolveFlow,
  type TurnRequest,
} from '#plugin-chat/lib/flow/index.ts';
import { toErrorMessage } from '#plugin-chat/lib/tool/errors/index.ts';
import type { ToolSidecarEntry } from '#plugin-chat/lib/tool/message-formatters.ts';

type FrontendToolsInput = Record<string, { description?: string; parameters: object }>;

/** Filters tools to only include enabled frontend tools, excluding disabled and backend-only tools. */
const getEnabledTools = (tools: Record<string, Tool>): FrontendToolsInput => {
  return Object.fromEntries(
    Object.entries(tools)
      .filter(([, tool]) => !tool.disabled && tool.type !== 'backend')
      .map(([name, tool]) => [name, { description: tool.description, parameters: tool.parameters ?? {} }]),
  );
};

/**
 * Custom chat transport that handles Atlas-specific message transformation and streaming.
 *
 * Renders the UI message thread to a prose-only model transcript via
 * {@link renderThreadTranscript} — tool call/result pairs are read
 * directly off the AI SDK's `ToolUIPart`/`DynamicToolUIPart` (already
 * paired keyed by `toolCallId`), so we never round-trip through
 * `convertToModelMessages` and lose the pairing.
 */
export class AtlasChatTransport extends AssistantChatTransport<AtlasMessage> implements ChatTransport<AtlasMessage> {
  private model: LanguageModel;
  private runtimeRef: AssistantRuntime | undefined;

  /**
   * Whether the user has requested the current agentic run to stop.
   *
   * Owned here (not in React) because the agentic tool loop is driven by
   * the AI SDK's `sendAutomaticallyWhen` continuation, which fires
   * asynchronously across the gap between two requests — a window where
   * the SDK's own `stop()` early-returns (status is transiently `ready`,
   * `activeResponse` is cleared) and does nothing. The transport is the
   * long-lived run-lifecycle owner, so the stop intent lives here and is
   * read by `shouldContinueAgentLoop` to gate the loop. Set via
   * {@link requestStop} on the stop button, cleared via
   * {@link clearStopRequest} on the next user-initiated send/regenerate
   * (loop continuations never pass through those entry points).
   */
  stopRequested = false;

  /** Mark the current agentic run as stopped by the user. */
  requestStop() {
    this.stopRequested = true;
  }

  /** Clear stop intent — called on a genuine user-initiated send/regenerate. */
  clearStopRequest() {
    this.stopRequested = false;
  }

  /**
   * The full flow-resolver set bridged in from React.
   *
   * The turn-flow engine is driven by resolvers registered through
   * `FlowContext`, which lives in React. The transport is the non-React
   * owner of the model call, so the resolvers are pushed here via
   * {@link setFlowResolvers} from the runtime hook (the same seam that calls
   * {@link setRuntime}) and read at `sendMessages` time to fold the turn
   * request. The set is composed with the always-on built-in `signalResolver`
   * upstream (via `engineResolvers`), so the same set drives both this request
   * seam and the loop-continuation seam.
   */
  private flowResolvers: FlowResolverEntry[] = [];

  override setRuntime(runtime: AssistantRuntime) {
    super.setRuntime(runtime);
    this.runtimeRef = runtime;
  }

  /**
   * Bridge the React-registered flow resolvers onto the transport. Called
   * from the runtime hook alongside {@link setRuntime}, so the non-React
   * model call can read the current resolver set when folding the turn.
   */
  setFlowResolvers(resolvers: FlowResolverEntry[]) {
    this.flowResolvers = resolvers;
  }

  constructor(model: LanguageModel, initOptions?: HttpChatTransportInitOptions<UIMessage>) {
    super(initOptions);
    console.info('Creating AtlasChatTransport with model', model);
    this.model = model;
  }

  /**
   * Validates and renders the UI message thread to a prose-only model
   * transcript. {@link renderThreadTranscript} owns the one-tree render
   * (every message is rendered — no recency cap); this method only adds
   * validation and the tail system reminder.
   */
  async processMessages(messages: AtlasMessage[], toolRegistry: Record<string, ToolSidecarEntry>) {
    console.debug('Processing messages for agent transport', { messages });
    const validationResult = await safeValidateUIMessages<AtlasMessage>({ messages });
    if (validationResult.success === false) {
      console.error('Message validation failed:', validationResult.error);
      throw new Error('Message validation failed', { cause: validationResult.error });
    }

    const transcript = renderThreadTranscript(validationResult.data, { toolRegistry });
    // Styled per-turn dump: each rendered model message in role color, so
    // the exact transcript the LLM sees can be eyeballed in the console.
    transcript.forEach((msg) => {
      console.debug(
        `%c[${msg.role}]\n%c${msg.content}`,
        'font-weight:bold; font-size:9px;',
        `color: oklch(0.8 0.1 ${msg.role === 'assistant' ? 217 : msg.role === 'user' ? 140 : 0}); font-size:9px; font-family:monospace;`,
      );
    });
    return transcript;
  }

  /**
   * Renders the UI thread to a prose-only transcript, folds the turn through
   * the flow engine, and streams the model's response.
   *
   * Tool registration relies on the runtime being attached via {@link setRuntime}; without it
   * no tools are exposed to the LLM and the call still runs as a plain text completion.
   */
  override async sendMessages(
    options: {
      chatId: string;
      messages: AtlasMessage[];
      abortSignal: AbortSignal | undefined;
    } & {
      trigger: 'submit-message' | 'regenerate-message';
      messageId: string | undefined;
    } & ChatRequestOptions,
  ): Promise<ReadableStream<UIMessageChunk>> {
    const modelContext = this.runtimeRef?.thread.getModelContext();
    const enabledTools = getEnabledTools(modelContext?.tools ?? {});
    const tools = frontendTools(enabledTools);
    try {
      // Pass the raw model-context tool registry through so processMessages
      // can read each tool's `message` formatters at transcript-build time.
      const toolRegistry = (modelContext?.tools ?? {}) as Record<string, ToolSidecarEntry>;
      const processedMessages = await this.processMessages(
        options.messages.filter((message) => message.parts.length > 0),
        toolRegistry,
      );

      // Fold the turn through the flow engine. The base request carries the
      // existing model-call inputs; the read-only transcript over the
      // incoming UI thread feeds the resolvers, whose transforms rewrite the
      // request (tool choice, active tools, ephemeral messages, system,
      // settings). `tools` is never replaced wholesale — narrowing is done
      // through `activeTools` only, keeping the assistant-ui UI binding.
      const baseRequest: TurnRequest = {
        model: this.model,
        system: modelContext?.system,
        messages: processedMessages,
        // biome-ignore lint/suspicious/noExplicitAny: frontendTools returns Schema<unknown>, AI SDK expects FlexibleSchema
        tools: tools as any as ToolSet,
        toolChoice: 'required',
        callSettings: modelContext?.callSettings ?? {},
      };
      const transcript = createTranscript(options.messages);
      const { transforms } = resolveFlow({ transcript, resolvers: this.flowResolvers });
      const request = await applyTurnRequest(baseRequest, transforms);

      const toolNames = Object.keys(enabledTools);
      console.debug('[AtlasChatTransport] sendMessages', {
        timestamp: dayjs().toISOString(),
        trigger: options.trigger,
        toolCount: toolNames.length,
        toolNames,
        tools,
        toolChoice: request.toolChoice,
        activeTools: request.activeTools,
        messageCount: options.messages.length,
        hasModelContext: !!modelContext,
        messages: { input: options.messages, processed: request.messages },
      });

      const result = streamText({
        model: request.model,
        tools: request.tools,
        toolChoice: request.toolChoice,
        activeTools: request.activeTools,
        system: request.system,
        messages: request.messages,
        abortSignal: options.abortSignal,
        ...request.callSettings,
      });

      return toUIMessageStream({
        stream: result.stream,
        onFinish: (...args: unknown[]) => {
          console.info('AtlasChatTransport stream finished', args);
        },
        originalMessages: options.messages,
        generateMessageId: createIdGenerator({ prefix: 'atlas' }),
        onError: (error: unknown) => {
          console.error('Error in AtlasChatTransport:', error);
          return toErrorMessage(error);
        },
      });
    } catch (error) {
      console.error('Error processing messages in AtlasChatTransport:', error);
      throw error;
    }
  }
}
